import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import MatchScoreCard from "@/components/shared/MatchScoreCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ROLE_CATEGORIES } from "@/pages/RegisterStaff";
import { TrendingUp, Lightbulb, ArrowRight } from "lucide-react";

const CATEGORY_LABEL = {
  licence: "Licence Status",
  role: "Role Match",
  qualifications: "Qualifications",
  experience: "Experience",
  kit: "Kit Available",
  location: "Location Match",
  availability: "Availability",
  endorsements: "Client Endorsements",
  completed_jobs: "Completed Jobs",
  reliability: "Reliability",
};

/**
 * Displays the current staff member's live match score with improvement tips.
 * Recalculates when the user picks a different role preset.
 */
export default function MyMatchScorePanel({ defaultRole = "" }) {
  const [role, setRole] = useState(defaultRole);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (role) params.set("role", role);
      const { data } = await api.get(`/staff/my-score${params.toString() ? "?" + params.toString() : ""}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, [role]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="card-flat p-6 space-y-4" data-testid="my-match-score-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5" /> Your Live Job Match Score
          </div>
          <h2 className="text-xl font-outfit font-semibold text-white mt-1">How companies see you right now.</h2>
          <p className="text-sm text-white/70 font-manrope max-w-xl mt-1">
            This is the score companies see when searching for your role in your county. Improve any area below to rank higher.
          </p>
        </div>
        <div className="min-w-[220px]">
          <Label className="text-xs">Score against role</Label>
          <Select value={role || "__primary__"} onValueChange={(v) => setRole(v === "__primary__" ? "" : v)}>
            <SelectTrigger data-testid="my-score-role-select"><SelectValue placeholder="My primary role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__primary__">My primary role</SelectItem>
              {ROLE_CATEGORIES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {busy && !data && <div className="text-white/60 text-sm font-manrope">Calculating…</div>}
      {data && (
        <>
          <MatchScoreCard match={data} size="md" showBreakdown={true} />

          {data.improvement_tips && data.improvement_tips.length > 0 && (
            <div className="rounded-lg border border-white/10 bg-[#0E0E0E] p-4" data-testid="improvement-tips">
              <div className="text-xs uppercase tracking-widest text-white font-outfit font-bold flex items-center gap-1 mb-2">
                <Lightbulb className="w-3.5 h-3.5 text-amber-500" /> How to improve your score
              </div>
              <div className="space-y-2">
                {data.improvement_tips.map((tip) => (
                  <div key={tip.category} className="flex items-start gap-3 text-sm font-manrope" data-testid={`tip-${tip.category}`}>
                    <div className="w-8 h-8 rounded-md bg-amber-50 border border-amber-200 text-amber-700 flex items-center justify-center flex-shrink-0">
                      <ArrowRight className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="font-outfit font-semibold text-white flex items-center justify-between gap-2">
                        <span>{CATEGORY_LABEL[tip.category] || tip.category}</span>
                        <span className="text-xs text-white/60 font-mono">{Math.round(tip.score)}%</span>
                      </div>
                      <div className="text-white/70 mt-0.5">{tip.message}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
